import { useEffect, useState } from "react";
import { Link } from "react-router";
import { isFeatureEnabled } from "@shared-core/config/features";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { useCanViewReplenishment } from "../../app/auth/hooks";

/**
 * The Stock page's tabs: "Stock items" and, where replenishment is on for
 * this user, "Replenishment" with the count of open proposals. Renders
 * nothing when there is only one tab to show.
 */
export function StockTabs({ active }: { active: "items" | "replenishment" }) {
  const dataAdapter = useDataAdapter();
  const canViewReplenishment = useCanViewReplenishment();
  const showReplenishment =
    isFeatureEnabled("stock_replenishment") && canViewReplenishment;
  const [openCount, setOpenCount] = useState<number | null>(null);

  useEffect(() => {
    if (!showReplenishment) return;
    let cancelled = false;
    dataAdapter
      .listReplenishmentProposals({ state: "open", pageSize: 1 })
      .then((page) => !cancelled && setOpenCount(page.total))
      .catch((error: unknown) => {
        console.error("stock.replenishment.count_load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, showReplenishment]);

  if (!showReplenishment) return null;

  const tabClass = (isActive: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
      isActive
        ? "bg-muted text-foreground font-medium"
        : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <nav aria-label="Stock" className="border-b border-border">
      <div className="flex flex-wrap gap-1 pb-2">
        <Link
          to={paths.stock()}
          className={tabClass(active === "items")}
          aria-current={active === "items" ? "page" : undefined}
        >
          Stock items
        </Link>
        <Link
          to={paths.stockReplenishment()}
          className={tabClass(active === "replenishment")}
          aria-current={active === "replenishment" ? "page" : undefined}
        >
          Replenishment
          {openCount != null && openCount > 0 && (
            <span
              className="rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-1.5 text-xs tabular-nums"
              aria-label={`${openCount} open`}
            >
              {formatCount(openCount)}
            </span>
          )}
        </Link>
      </div>
    </nav>
  );
}
